import React, { useState } from 'react';
import { TFunction } from 'i18next';
import { z } from 'zod';
import { match } from 'ts-pattern';
import { differenceInSeconds } from 'date-fns';

import {
  Button,
  Card,
  Flex,
  Dialog,
  Notice,
  ProgressIndicator,
  Text,
  useToasts,
  Heading,
} from '@applyboard/crystal-ui';
import {
  DocumentAlertHifiIcon,
  ListCheckOutlineIcon,
  RefreshOutlineIcon,
  CheckMarkOutlineIcon,
  RefreshingOutlineIcon,
  ErrorAltOutlineIcon,
} from '@applyboard/ui-icons';
import { useEventTracker, useTranslation } from '@applyboard/ui-utils';

import { canPerformAction } from '~/utils/actions';
import { assert } from '~/utils/assert';
import { LightBulbAndGearsIllustration } from '~/illustrations';
import { convertArrayToMap } from '~/utils/convertArrayToMap';
import { useNow } from '~/utils/hooks';
import {
  DynamicForm,
  DynamicFormEvent,
  FormDefinition,
  UnknownPaymentError,
  AlreadyPaidError,
  InactiveOrderError,
  useResetForm,
  useIsVerifyingFiles,
  PaymentFrameStatus,
  usePaymentStatus,
  useIsReviewing,
  isFieldComponent,
} from '~/components/DynamicForm';
import { DescriptionAndLinks } from '~/components/RequirementCard';
import { ApplicationRequirement } from '~/types';
import {
  convertDynamicFormToResponseAnswer,
  convertResponsesToDynamicForm,
  getFormDataDecisions,
} from './ApplicationRequirementCard.utils';
import { generateFormFileInformation } from '../RequirementList.utils';

import {
  useApplication,
  useApplicationRequirements,
  useSendRequirementAction,
  useUploadRequirementFile,
  useCreateOrder,
  usePollRequirement,
  useUpdateApplicationRequirement,
  IncorrectETagError,
} from '../../../data';
import { useRequirementListEventTracker } from '../../../utils/eventTracking';
import { useTheme } from '@emotion/react';

export function ProvideAnswersButton({
  requirementId,
  onOpenChange,
  defaultOpen,
  fullScreen,
  onSubmissionSuccess,
}: {
  requirementId: string;
  onOpenChange?: (isOpen: boolean) => void;
  defaultOpen?: boolean;
  fullScreen?: boolean;
  onSubmissionSuccess?: (requirementId: string) => void;
}) {
  const { setNextUpdate, updateStatus, submitRequirement, submitRequirementStatus } =
    useFulfillRequirement({ requirementId });

  const { uploadRequirementFileAsync, isUploadingRequirementFile } = useUploadRequirementFile();
  const { application } = useApplication();
  const { t } = useTranslation('ProvideAnswersButton');
  const toasts = useToasts();
  const [isVerifyingWarningOpen, setIsVerifyingWarningOpen] = useState(false);
  const { createOrder } = useCreateOrder();
  const { applicationRequirements } = useApplicationRequirements();
  const { trackDynamicFormEvent } = useDynamicFormEventTracking({ requirementId });
  const { trackEvent } = useRequirementListEventTracker();

  const requirement = applicationRequirements.find(req => req.id === requirementId);
  assert(requirement);

  const [originalRequirement, setOriginalRequirement] = useState<typeof requirement | null>(
    defaultOpen ? requirement : null,
  );
  const { id, formDefinition, type, title, responses } = requirement;
  const answersHistory = responses?.answersHistory;
  const formDataAnswers =
    Array.isArray(answersHistory) && answersHistory.length > 0 && answersHistory[0].answers;

  function handleChange({
    getValues,
    setValues,
  }: {
    getValues: () => Record<string, unknown>;
    setValues: (values: Record<string, unknown>) => void;
  }) {
    const answers = convertDynamicFormToResponseAnswer({
      formDefinition,
      dynamicFormValues: getValues(),
    });

    setNextUpdate(
      { id, type, answers },
      {
        onSuccess: ({ permanentIds }) =>
          setValues(
            replaceTempFileIdsWithPermanentIds({
              fieldValues: getValues(),
              permanentIds,
              formDefinition,
            }),
          ),
        onError: err => toasts.negative(err),
      },
    );
  }

  const eventType = getEventType({ requirement });
  const isPaymentFrame = formDefinition.components.every(
    component => component.type === 'paymentframe',
  );
  const adminRejectionText =
    requirement.classification === 'applicant_requirement' ? undefined : t('adminRejectionText');
  const formDataDecisions = answersHistory
    ? getFormDataDecisions({ answersHistory, adminRejectionText })
    : undefined;

  return (
    <DynamicForm
      aria-label={title}
      formData={
        Array.isArray(formDataAnswers) ? convertResponsesToDynamicForm(formDataAnswers) : undefined
      }
      formDataDecisions={formDataDecisions}
      formDefinition={formDefinition}
      formFileInformation={generateFormFileInformation(requirement)}
      onChange={handleChange}
      isSaving={updateStatus === 'loading' || submitRequirementStatus === 'loading'}
      onSubmit={formValues => {
        const prefix =
          requirement.classification === 'applicant_requirement'
            ? t('requirement')
            : t('adminRequirement');
        const message = t('successToast', {
          title: title,
          interpolation: { escapeValue: false },
        });
        submitRequirement({
          formValues,
          onSuccess: () => {
            toasts.positive(`${prefix} ${message}`);
            onSubmissionSuccess?.(requirementId);
            setOriginalRequirement(null);
            trackEvent({
              event: `${eventType} - Clicked ${isPaymentFrame ? 'pay' : 'submit'}`,
            });
            onOpenChange?.(false);
          },
        });
      }}
      uploadFile={(file, { onSuccess, onError }) => {
        uploadRequirementFileAsync({ applicationId: application.id, requirementId: id, file })
          .then(response => {
            trackEvent({
              event: `${eventType} - Clicked browse files`,
            });
            onSuccess(response.data.id);
          })
          .catch(error => {
            if (error instanceof Error) {
              toasts.negative(error);
            } else {
              toasts.negative(new Error('An error occurred while uploading the file.'));
            }
            onError();
          });
      }}
      onAttemptNextWhileVerifying={() => setIsVerifyingWarningOpen(true)}
      onRequestCreateOrder={({ onSuccess, onError }) => {
        createOrder(requirement.id, {
          onSuccess: response => onSuccess(response.data.id),
          onError: err => {
            if (
              err instanceof AlreadyPaidError ||
              err instanceof InactiveOrderError ||
              err instanceof UnknownPaymentError
            ) {
              onError(err);
            } else {
              onError(new UnknownPaymentError());
            }
          },
        });
      }}
      onRequestConfirmPayment={({ onSuccess, onError }) => {
        setNextUpdate(
          { id: id, type, status: 'approved' },
          {
            onSuccess,
            onError: () => {
              onError(new UnknownPaymentError('Error confirming payment'));
            },
          },
        );
      }}
      onClose={() => {
        if (originalRequirement?.status !== 'approved' && requirement.status === 'approved') {
          const prefix =
            requirement.classification === 'applicant_requirement'
              ? t('requirement')
              : t('adminRequirement');
          const message = t('successfullyApprovedMessage', { requirementTitle: requirement.title });
          toasts.positive(`${prefix} ${message}`);
        }
        setOriginalRequirement(null);
        onOpenChange?.(false);
      }}
      onEvent={trackDynamicFormEvent}
      // Don't allow the user to proceed while we're still attaching the files
      // to the requirement.  Otherwise, the user could proceed while file
      // verification is queuing
      blockProceedWhenMissingFileInfo
    >
      <RequirementForm
        isOpen={!!originalRequirement}
        onOpenChange={newOpen => {
          if (newOpen) {
            setOriginalRequirement(requirement);
          } else {
            setOriginalRequirement(null);
          }
          onOpenChange?.(newOpen);
        }}
        requirement={requirement}
        saveStatus={<SaveStatus status={isUploadingRequirementFile ? 'loading' : updateStatus} />}
        onAttemptCloseWhileVerifying={() => {
          setIsVerifyingWarningOpen(true);
          trackEvent({
            event: `${eventType} - Attempted to interrupt post-upload checks`,
          });
        }}
        fullScreen={fullScreen}
      />
      <VerificationWarningDialog
        open={isVerifyingWarningOpen}
        onClose={setIsVerifyingWarningOpen}
      />
    </DynamicForm>
  );
}

function useFulfillRequirement({ requirementId }: { requirementId: string }) {
  const { applicationRequirements } = useApplicationRequirements();
  const requirement = applicationRequirements.find(req => req.id === requirementId);
  assert(requirement);

  const { setNextUpdate, updateStatus } = useUpdateApplicationRequirement();
  const { sendRequirementAction, sendRequirementActionStatus } = useSendRequirementAction();
  const { t } = useTranslation('ProvideAnswersButton');
  const toasts = useToasts();
  const action = requirement.classification === 'applicant_requirement' ? 'submit' : 'approve';

  const handleSubmit = ({ onSuccess }: { onSuccess: () => void }) => {
    sendRequirementAction({
      action,
      requirementId: requirement.id,
      options: {
        onSuccess,
        onError: err => {
          toasts.negative(
            err instanceof IncorrectETagError ? err : new Error(t('submitErrorToast')),
          );
        },
      },
    });
  };

  const submitRequirement = ({
    formValues,
    onSuccess,
  }: {
    formValues: Record<string, unknown>;
    onSuccess: () => void;
  }) => {
    if (
      // This checks to make sure form answers have been submitted
      // successfully via PATCH. Certain formDefinitions with default values
      // may allow the user to reach the submit button without triggering
      // the update PATCH. This can cause errors on the /actions POST.
      // See: https://applyboard.atlassian.net/browse/AWA-5849
      updateStatus === 'error' ||
      !requirement.responses?.answersHistory?.length ||
      requirement.responses.answersHistory[0].submitted
    ) {
      const answers = convertDynamicFormToResponseAnswer({
        formDefinition: requirement.formDefinition,
        dynamicFormValues: formValues,
      });
      setNextUpdate(
        { id: requirement.id, type: requirement.type, answers },
        {
          onSuccess: () => handleSubmit({ onSuccess }),
          onError: err => {
            toasts.negative(
              err instanceof IncorrectETagError ? err : new Error(t('submitErrorToast')),
            );
          },
        },
      );
    } else {
      handleSubmit({ onSuccess });
    }
  };

  return {
    setNextUpdate,
    updateStatus,
    submitRequirement,
    submitRequirementStatus: sendRequirementActionStatus,
  };
}

function RequirementForm({
  isOpen,
  onOpenChange,
  requirement,
  saveStatus,
  onAttemptCloseWhileVerifying,
  fullScreen,
}: {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  requirement: ApplicationRequirement;
  saveStatus: React.ReactNode;
  onAttemptCloseWhileVerifying: () => void;
  fullScreen?: boolean;
}) {
  const { t } = useTranslation('ApplicationRequirementCard');
  const { trackEvent } = useRequirementListEventTracker();
  const eventType = getEventType({ requirement });
  const { id, title, status, formDefinition } = requirement;
  const paymentStatus = usePaymentStatus();
  const { actionText, dialogTitle } = getCopy({ requirement, paymentStatus, t });
  const resetForm = useResetForm();
  const isVerifyingFiles = useIsVerifyingFiles();
  const isReviewing = useIsReviewing();
  const isAdminRequirement = requirement.classification !== 'applicant_requirement';

  const flowEnded = match(paymentStatus)
    .with('already_paid', 'payment_success', 'unknown_error', 'inactive', () => true)
    .with('confirming_payment', 'creating_order', 'user_paying', () => false)
    .exhaustive();

  const canFulfill = isAdminRequirement
    ? canPerformAction(requirement, 'approve')
    : canPerformAction(requirement, 'submit');

  usePollRequirement(id, {
    enabled: isOpen && isVerifyingFiles,
  });

  const reviewAnswersI18nKey = isAdminRequirement
    ? 'reviewAllProvidedResponsesWithRequirementAdmin'
    : 'reviewAllProvidedResponsesWithRequirement';

  const FormRoot = fullScreen ? FullScreenFormRoot : Dialog;
  const FormHeading = fullScreen ? FullScreenFormHeading : Dialog.Heading;
  const FormContent = fullScreen ? React.Fragment : Dialog.Content;
  const FormActions = fullScreen ? React.Fragment : Dialog.Actions;

  return (
    <FormRoot
      open={isOpen}
      onOpenChange={newIsOpen => {
        if (isVerifyingFiles && !newIsOpen) {
          onAttemptCloseWhileVerifying();
          return;
        }
        onOpenChange(newIsOpen);
      }}
      size={flowEnded ? 'md' : 'lg'}
    >
      {!fullScreen && status !== 'approved' && status !== 'reviewing' && status !== 'inactive' && (
        <Dialog.Trigger>
          <Button
            emphasis="contained"
            intent="primary"
            leadIcon={status === 'rejected' ? RefreshOutlineIcon : ListCheckOutlineIcon}
            onClick={() => {
              resetForm();
              trackEvent({
                event: `Clicked ${eventType.toLowerCase()}`,
              });
            }}
            width={{ xs: 'fill', sm: 'hug' }}
          >
            {actionText}
          </Button>
        </Dialog.Trigger>
      )}
      <FormHeading>
        {dialogTitle}
        {formDefinition.components.some(isFieldComponent) && saveStatus}
      </FormHeading>
      <FormContent>
        {fullScreen && status === 'reviewing' ? (
          <Text>Requirement has been successfully submitted</Text>
        ) : status === 'preparing' ? (
          <RequirementPreparingState requirementId={id} />
        ) : canFulfill ? (
          <Flex direction="column" gap={4}>
            {!isReviewing && !flowEnded && (
              <>
                <Text>{getInstructions({ formDefinition, t, isAdminRequirement })}</Text>
                <Card>
                  <div css={{ maxHeight: '120px', overflowY: 'auto' }}>
                    <DescriptionAndLinks
                      requirement={requirement}
                      headingSettings={{ baseLevel: fullScreen ? 2 : 3 }}
                      disableTruncation
                    />
                  </div>
                </Card>
              </>
            )}
            <DynamicForm.Form
              noticeText={isAdminRequirement ? t('submitAndApproveNotice') : undefined}
              instructions={t(reviewAnswersI18nKey, { requirementName: title })}
            />
          </Flex>
        ) : (
          <Notice intent="secondary">{t('youDoNotHavePermission')}</Notice>
        )}
      </FormContent>
      {status !== 'preparing' && canFulfill && (
        <FormActions>
          <DynamicForm.Actions
            submitLabel={isAdminRequirement ? t('submitAndApprove') : undefined}
          />
        </FormActions>
      )}
    </FormRoot>
  );
}

/**
 * The root wrapper component for use when the form is displayed in full screen
 */
function FullScreenFormRoot({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ backgroundColor: 'white', height: '100vh', width: '100vw' }}>
      <Flex direction="column" gap={2}>
        {children}
      </Flex>
    </div>
  );
}

/**
 * The form heading for use when the form is displayed in full screen
 */
function FullScreenFormHeading({ children }: { children: React.ReactNode }) {
  return (
    <Heading level={1} variant="titleM">
      {children}
    </Heading>
  );
}

export function RequirementPreparingState({ requirementId }: { requirementId: string }) {
  const { t } = useTranslation('ApplicationRequirementCard');
  const { estimatedTimeUntilPrepared } = usePollRequirement(requirementId);
  const now = useNow();
  const preparingTime = estimatedTimeUntilPrepared
    ? differenceInSeconds(new Date(estimatedTimeUntilPrepared), now)
    : 0;
  return (
    <Flex align="center" justify="center" direction="column" p={{ xs: 0, md: 24 }} gap={8}>
      <LightBulbAndGearsIllustration />
      <ProgressIndicator
        alignment="default"
        helpText={
          preparingTime > 0
            ? t('preparingStateEstimatedTime', {
                preparingTime,
                preparingTimePlural: preparingTime > 1 ? 's' : '',
              })
            : t('preparingStateJustAMoment')
        }
        indeterminate
        label={t('preparingStateLabel')}
        size="sm"
        variant="linear"
      />
    </Flex>
  );
}

function SaveStatus({ status }: { status: 'idle' | 'loading' | 'success' | 'error' }) {
  const { t } = useTranslation('ApplicationRequirementCard');
  const theme = useTheme();

  return (
    <span role="status" css={{ marginLeft: theme.spacing[4] }}>
      {match(status)
        .with('idle', () => null)
        .with('loading', () => (
          <Text as="span" intent="secondary" contrast="low">
            <span css={{ position: 'relative', top: '-1px', marginRight: theme.spacing[2] }}>
              <RefreshingOutlineIcon intent="secondary" contrast="low" />
            </span>
            {t('statusSaving')}
          </Text>
        ))
        .with('success', () => (
          <Text as="span" intent="secondary" contrast="low">
            <span css={{ position: 'relative', top: '-1px', marginRight: theme.spacing[2] }}>
              <CheckMarkOutlineIcon intent="secondary" contrast="low" />
            </span>
            {t('statusSaved')}
          </Text>
        ))
        .with('error', () => (
          <Text as="span" intent="negative">
            <span css={{ position: 'relative', top: '-1px', marginRight: theme.spacing[2] }}>
              <ErrorAltOutlineIcon intent="negative" />
            </span>
            {t('statusSavingError')}
          </Text>
        ))
        .exhaustive()}
    </span>
  );
}

function VerificationWarningDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: (isOpen: boolean) => void;
}) {
  const { t } = useTranslation('ApplicationRequirementCard');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <Dialog.Heading>{t('validationWarningHeader')}</Dialog.Heading>
      <Dialog.Content>
        <Flex direction="row" gap={4}>
          <DocumentAlertHifiIcon intent="warning" />
          <Text>{t('validationWarningText')}</Text>
        </Flex>
      </Dialog.Content>
      <Dialog.Actions>
        <Flex direction="column" grow={1}>
          <Flex.Item align="end">
            <Dialog.CloseButton emphasis="contained" intent="primary" width="hug">
              {t('validationConfirmationButtonText')}
            </Dialog.CloseButton>
          </Flex.Item>
        </Flex>
      </Dialog.Actions>
    </Dialog>
  );
}

/**
 * Gets the instructions for the form
 */
function getInstructions({
  formDefinition,
  t,
  isAdminRequirement,
}: {
  formDefinition: FormDefinition;
  t: TFunction<'ApplicationRequirementCard'>;
  isAdminRequirement?: boolean;
}) {
  if (formDefinition.components.some(component => component.type === 'paymentframe')) {
    return t(`instructionsPaymentFrame${isAdminRequirement ? 'Admin' : ''}`);
  }

  if (formDefinition.components.some(component => component.type === 'paymentprompt')) {
    return t(`instructionsPaymentPrompt${isAdminRequirement ? 'Admin' : ''}`);
  }

  return t(`instructions${isAdminRequirement ? 'Admin' : ''}`);
}

/**
 * Gets the most appropriate button label, given the form definition
 */
function getCopy({
  requirement,
  paymentStatus,
  t,
}: {
  requirement: Pick<ApplicationRequirement, 'formDefinition' | 'status'>;
  paymentStatus: PaymentFrameStatus;
  t: TFunction<'ApplicationRequirementCard'>;
}) {
  const { status, formDefinition } = requirement;

  if (formDefinition.components.every(component => component.type === 'file')) {
    return status === 'rejected'
      ? { actionText: t('reviseDocuments'), dialogTitle: t('reviseDocumentsTitle') }
      : { actionText: t('provideDocuments'), dialogTitle: t('provideDocumentsTitle') };
  }

  if (
    formDefinition.components.every(
      component => component.type === 'paymentprompt' || component.type === 'file',
    )
  ) {
    // TODO: is "Revise payment" the correct copy when status is rejected?
    return status === 'rejected'
      ? { actionText: t('revisePayment'), dialogTitle: t('revisePaymentTitle') }
      : { actionText: t('providePayment'), dialogTitle: t('providePaymentTitle') };
  }

  if (formDefinition.components.every(component => component.type === 'paymentframe')) {
    return {
      actionText: t('pay'),
      dialogTitle: match(paymentStatus)
        .with('inactive', () => t('paymentErrorTitleRequirementRemoved'))
        .with('already_paid', () => t('paymentErrorTitleAlreadyPaid'))
        .with('unknown_error', () => t('paymentErrorTitleUnknownError'))
        .with('payment_success', () => t('paymentSuccessTitle'))
        .with('confirming_payment', 'creating_order', 'user_paying', () => t('payTitle'))
        .exhaustive(),
    };
  }

  return status === 'rejected'
    ? { actionText: t('reviseAnswers'), dialogTitle: t('reviseAnswersTitle') }
    : { actionText: t('provideAnswers'), dialogTitle: t('provideAnswersTitle') };
}

/**
 * Given some field values, replaces any temporary IDs with any known permanent
 * IDs
 */
function replaceTempFileIdsWithPermanentIds({
  fieldValues,
  permanentIds,
  formDefinition,
}: {
  fieldValues: Record<string, unknown>;
  permanentIds: Record<string, string | undefined>;
  formDefinition: FormDefinition;
}) {
  const fileSchema = z.array(z.union([z.string(), z.object({ id: z.string() }).passthrough()]));
  const componentMap = convertArrayToMap(formDefinition.components, 'key');

  const newFieldValues: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(fieldValues)) {
    const component = componentMap[key];
    if (component.type !== 'file') {
      continue;
    }

    const parsedValue = fileSchema.parse(value);
    newFieldValues[key] = parsedValue.map(item => {
      const possiblyTempFileId = typeof item === 'string' ? item : item.id;
      const newFileId = permanentIds[possiblyTempFileId] || item;
      return newFileId;
    });
  }

  return newFieldValues;
}

function getEventType({ requirement }: { requirement: ApplicationRequirement }) {
  const { status, formDefinition } = requirement;

  if (formDefinition.components.every(component => component.type === 'paymentframe')) {
    return 'Pay';
  }

  const isRejected = status === 'rejected';
  const isOnlyFiles = formDefinition.components.every(component => component.type === 'file');
  const isPaymentPrompt = formDefinition.components.every(
    component => component.type === 'paymentprompt' || component.type === 'file',
  );
  let responseType = 'answers';

  if (isPaymentPrompt) {
    responseType = 'payment';
  }
  if (isOnlyFiles) {
    responseType = 'documents';
  }

  const fulfillType = isRejected ? 'Revise' : 'Provide';
  return `${fulfillType} ${responseType}`;
}

function useDynamicFormEventTracking({ requirementId }: { requirementId: string }) {
  const { applicationRequirements } = useApplicationRequirements();
  const { trackEvent } = useEventTracker();
  const eventNameBase = 'Single Application V2 - Applicant Requirements';
  const requirement = applicationRequirements.find(requirement => requirement.id === requirementId);
  assert(requirement);
  const eventType = getEventType({ requirement });
  const trackDynamicFormEvent = (eventName: DynamicFormEvent) => {
    const eventData = match(eventName)
      .with('removed_file_with_warning', () => ({
        event: `${eventNameBase} - Provide Documents - Acted on the warnings (Removed file/uploaded new file)`,
        data: {
          actionType: 'Removed file',
        },
      }))
      .with('uploaded_new_file_to_file_with_warning', () => ({
        event: `${eventNameBase} - Provide Documents - Acted on the warnings (Removed file/uploaded new file)`,
        data: {
          actionType: 'Uploaded new file',
        },
      }))
      .with('view_file_warning', () => ({
        event: `${eventNameBase} - Provide Documents - Viewed Post upload warnings`,
      }))
      .with('clicked_pay_via_flywire', () => ({
        event: `${eventNameBase} - Provide payment - Clicked pay via flywire`,
      }))
      .with('copied_payment_link', () => ({
        event: `${eventNameBase} - Provide payment - Clicked copy payment link`,
      }))
      .with('cancel_payment', () => ({
        event: `${eventNameBase} - Pay - Clicked cancel`,
      }))
      .with('clicked_proceed', () => ({
        event: `${eventNameBase} - ${eventType} - Clicked proceed anyway`,
      }))
      .with('clicked_review', () => ({
        event: `${eventNameBase} - ${eventType} - Clicked review`,
      }))
      .with('clicked_next', () => ({
        event: `${eventNameBase} - ${eventType} - Clicked next`,
      }))
      .exhaustive();

    trackEvent({ ...eventData, group: 'Single Application V2' });
  };

  return {
    trackDynamicFormEvent,
  };
}
